import { createRouter, createWebHistory } from "vue-router";

import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { usePermissionsStore } from "@/stores/permissionsStore";
import { useUserDataStore } from "@/stores/userDataStore";

import { reloadPageOnceAfterChunkLoadError } from "@/utils/chunkLoadErrorUtils";
import { getCookie } from "@/utils/cookieUtils";
import { isDebugFeatureEnabled } from "@/utils/debugFeatureUtils";
import { can } from "@/utils/permissionUtils";
import { PAGE_ACCESS_KEYS, ROUTE_ACTION_PERMISSIONS } from "@/utils/routePermissions";

import HomePage from "@/views/HomePage";
import AnalyticsPage from "@/views/AnalyticsPage.vue";
import NotFoundPage from "@/views/NotFoundPage";
import ForbiddenPage from "@/views/ForbiddenPage.vue";
import CounterpartiesPage from "@/views/CounterpartiesPage.vue";
import CounterpartyDetailsPage from "@/views/CounterpartyDetailsPage.vue";
import CounterpartyCreatePage from "@/views/CounterpartyCreatePage.vue";
import TripsPage from "@/views/TripsPage.vue";
import ChatsPage from "@/views/ChatsPage.vue";
import DriversPage from "@/views/DriversPage.vue";
import VehiclesPage from "@/views/VehiclesPage.vue";
import IntegrationsPage from "@/views/IntegrationsPage.vue";
import EdoDocumentsPage from "@/views/EdoDocumentsPage.vue";
import EdoDocumentDetailsPage from "@/views/EdoDocumentDetailsPage.vue";
import SettingsPage from "@/views/SettingsPage.vue";
import ExpensesPage from "@/views/ExpensesPage.vue";
import LoginPage from "@/views/LoginPage.vue";
import UsersPage from "@/views/UsersPage.vue";
import RequisiteTypesPage from "@/views/RequisiteTypesPage.vue";
import PasswordRecoveryPage from "@/views/PasswordRecoveryPage.vue";
import AccountRecoveryPage from "@/views/AccountRecoveryPage.vue";
import OrdersPage from "@/views/OrdersPage.vue";
import OrderDetailsPage from "@/views/OrderDetailsPage.vue";
import ExpenseCategoriesPage from "@/views/ExpenseCategoriesPage.vue";
import TemplatesPage from "@/views/TemplatesPage.vue";
import ProductsPage from "@/views/ProductsPage.vue";
import CatalogItemsPage from "@/views/CatalogItemsPage.vue";
import CargoUnitsPage from "@/views/CargoUnitsPage.vue";
import CargoPropertiesPage from "@/views/CargoPropertiesPage.vue";
import CargoItemRegistriesPage from "@/views/CargoItemRegistriesPage.vue";
import ReferenceItemsPage from "@/views/ReferenceItemsPage.vue";
import PaymentSourcesPage from "@/views/PaymentSourcesPage.vue";
import PermissionRolesPage from "@/views/PermissionRolesPage.vue";
import PermissionRoleDetailsPage from "@/views/PermissionRoleDetailsPage.vue";
import WaybillsPage from "@/views/WaybillsPage.vue";

const edoFormMeta = (title) => ({
  requiresAuth: true,
  title,
  hasFixedFormFooter: true,
});

const previewRoutes =
  process.env.NODE_ENV === "development"
    ? [
        {
          path: "/notifications-preview",
          name: "NotificationPreview",
          component: () =>
            import("@/views/NotificationPreviewPage.vue"),
          meta: {
            requiresAuth: false,
            title: "Превью уведомлений",
          },
        },
      ]
    : [];

const routes = [
  ...previewRoutes,
  {
    path: "/notifications",
    component: () => import("@/views/NotificationsLayout.vue"),
    meta: { requiresAuth: true, title: "Уведомления" },
    children: [
      {
        path: "",
        name: "Notifications",
        component: () => import("@/views/NotificationsPage.vue"),
      },
      {
        path: "settings",
        name: "NotificationSettings",
        component: () => import("@/views/NotificationSettingsPage.vue"),
        meta: { title: "Настройки уведомлений" },
      },
    ],
  },
  {
    path: "/communication",
    name: "CommunicationPosts",
    component: () => import("@/views/CommunicationPostsPage.vue"),
    meta: { requiresAuth: true, title: "Коммуникация" },
  },
  {
    path: "/communication/posts/new",
    name: "CommunicationPostCreate",
    component: () => import("@/views/CommunicationPostPage.vue"),
    meta: { requiresAuth: true, title: "Новая статья", hasFixedFormFooter: true },
  },
  {
    path: "/communication/posts/:id",
    name: "CommunicationPostDetails",
    component: () => import("@/views/CommunicationPostPage.vue"),
    props: true,
    meta: { requiresAuth: true, title: "Управление статьёй", hasFixedFormFooter: true },
  },
  {
    path: "/settings/communication_tags",
    name: "CommunicationTags",
    component: () => import("@/views/CommunicationTagsPage.vue"),
    meta: { requiresAuth: true, title: "Настройки | Теги блога" },
  },
  {
    path: "/warehouses",
    name: "Warehouse",
    component: () => import("@/views/WarehousePage.vue"),
    meta: { requiresAuth: true, title: "Склад" },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFoundPage,
    meta: { requiresAuth: false, title: "Страница не найдена" },
  },
  {
    path: "/forbidden",
    name: "Forbidden",
    component: ForbiddenPage,
    meta: { requiresAuth: false, title: "Доступ запрещён" },
  },
  {
    path: "/",
    redirect: "/home",
    meta: { requiresAuth: true },
  },
  {
    path: "/home",
    name: "Home",
    component: HomePage,
    meta: { requiresAuth: true, title: "Главная" },
  },
  {
    path: "/no-companies",
    name: "NoCompanies",
    component: () => import("@/views/NoCompaniesPage.vue"),
    meta: { requiresAuth: true, title: "Нет доступных компаний" },
  },
  {
    path: "/analytics",
    name: "Analytics",
    component: AnalyticsPage,
    meta: { requiresAuth: true, title: "Статистика" },
  },
  {
    path: "/activity-log",
    name: "ActivityLog",
    component: () => import("@/views/ActivityLogPage.vue"),
    meta: { requiresAuth: true, title: "Журнал событий" },
  },
  {
    path: "/settings/users/invite",
    alias: "/users/invite",
    name: "UserInvite",
    component: () => import("@/views/UserInvitePage.vue"),
    meta: { requiresAuth: true, title: "Приглашение пользователя" },
  },
  {
    path: "/settings/users/new",
    alias: "/users/new",
    name: "UserNew",
    component: () => import("@/views/UserNewPage.vue"),
    meta: { requiresAuth: true, title: "Новый пользователь" },
  },
  {
    path: "/settings/users/invite/success",
    alias: "/users/invite/success",
    name: "UserInviteSuccess",
    component: () => import("@/views/UserInviteSuccessPage.vue"),
    meta: { requiresAuth: true, title: "Пользователь создан" },
  },
  {
    path: "/settings/users/invite/expired",
    alias: "/users/invite/expired",
    name: "UserInviteExpired",
    component: () => import("@/views/UserInviteExpiredPage.vue"),
    meta: { requiresAuth: true, title: "Данные приглашения скрыты" },
  },
  {
    path: "/settings/users",
    alias: "/users",
    name: "Users",
    component: UsersPage,
    meta: { requiresAuth: true, title: "Пользователи" },
  },
  {
    path: "/settings/users/:id",
    alias: "/users/:id",
    name: "UserDetails",
    component: () => import("@/views/UserDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Пользователи" },
    props: true,
  },
  {
    path: "/counterparties",
    name: "Counterparties",
    component: CounterpartiesPage,
    meta: { requiresAuth: true, title: "Контрагенты" },
  },
  {
    path: "/counterparties/:id",
    name: "CounterpartyDetails",
    component: CounterpartyDetailsPage,
    meta: { requiresAuth: true, title: "Контрагент" },
    props: true,
  },
  {
    path: "/counterparties/create",
    name: "CounterpartyCreate",
    component: CounterpartyCreatePage,
    meta: { requiresAuth: true, title: "Новый контрагент" },
  },
  {
    path: "/orders",
    name: "Orders",
    component: OrdersPage,
    meta: { requiresAuth: true, title: "Заказы" },
  },
  {
    path: "/sharing",
    name: "Sharings",
    component: () => import("@/views/SharingsPage.vue"),
    meta: { requiresAuth: true, title: "Общий доступ" },
  },
  {
    path: "/sharing/new",
    name: "SharingCreate",
    component: () => import("@/views/SharingEditorPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Новая публичная ссылка",
      hasFixedFormFooter: true,
    },
  },
  {
    path: "/sharing/:id/edit",
    name: "SharingEdit",
    component: () => import("@/views/SharingEditorPage.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      title: "Настройка публичной ссылки",
      hasFixedFormFooter: true,
    },
  },
  {
    path: "/share/:token",
    name: "PublicSharing",
    component: () => import("@/views/PublicSharingPage.vue"),
    props: true,
    meta: { requiresAuth: false, title: "Общий доступ" },
  },
  {
    path: "/orders/:id",
    name: "OrderDetails",
    component: OrderDetailsPage,
    meta: {
      requiresAuth: true,
      title: "Заказ",
      hasFixedFormFooter: true,
    },
    props: true,
  },
  {
    path: "/orders/create",
    name: "OrderCreate",
    component: () => import("@/views/OrderCreatePage.vue"),
    meta: {
      requiresAuth: true,
      title: "Новый заказ",
      hasFixedFormFooter: true,
    },
    children: [
      {
        path: "",
        name: "OrderCreateStep1",
        component: () =>
          import("@/components/Order/Step1MainInfo.vue"),
      },
      {
        path: "cargo",
        name: "OrderCreateStep2",
        component: () => import("@/components/Order/Step2Cargo.vue"),
      },
      {
        path: "points",
        name: "OrderCreateStep3",
        component: () => import("@/components/Order/Step2Points.vue"),
      },
      {
        path: "trip",
        name: "OrderCreateStep4",
        component: () => import("@/components/Order/Step3Trip.vue"),
      },
    ],
  },
  {
    path: "/trips",
    name: "Trips",
    component: TripsPage,
    meta: { requiresAuth: true, title: "Рейсы" },
  },
  {
    path: "/trips/create",
    name: "TripCreate",
    component: () => import("@/views/TripCreatePage.vue"),
    meta: { requiresAuth: true, title: "Новый рейс" },
  },
  {
    path: "/trips/:id",
    name: "TripDetails",
    component: () => import("@/views/TripDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Рейс" },
    props: true,
  },
  {
    path: "/trips/:id/cargos/:cargoId",
    name: "TripCargoDetails",
    component: () => import("@/views/TripCargoDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Груз" },
    props: true,
  },
  {
    path: "/operational/calculator",
    name: "OperationalCalculator",
    component: () => import("@/views/OperationalCalculatorPage.vue"),
    meta: { requiresAuth: true, title: "Калькулятор" },
  },
  {
    path: "/settings/calculator-presets",
    name: "CalculatorPresets",
    component: () => import("@/views/CalculatorPresetsPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Настройки калькуляторов",
    },
  },
  {
    path: "/settings/calculator-presets/new",
    name: "CalculatorPresetCreate",
    component: () => import("@/views/CalculatorPresetCreatePage.vue"),
    meta: {
      requiresAuth: true,
      title: "Новый калькулятор",
    },
  },
  {
    path: "/settings/calculator-presets/:id",
    name: "CalculatorPresetDetails",
    component: () =>
      import("@/views/CalculatorPresetDetailsPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Настройки калькулятора",
    },
    props: true,
  },
  {
    path: "/waybills",
    name: "Waybills",
    component: WaybillsPage,
    meta: { requiresAuth: true, title: "Путевые листы" },
  },
  {
    path: "/waybills/:id",
    name: "WaybillDetails",
    component: () => import("@/views/WaybillDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Путевой лист" },
    props: true,
  },
  {
    path: "/chats",
    name: "Chats",
    component: ChatsPage,
    meta: {
      requiresAuth: true,
      title: "Чаты",
      forceCollapseSupportWidget: true,
    },
  },

  {
    path: "/debug",
    name: "Debug",
    component: () => import("@/views/DebugPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Debug",
      debugOnly: true,
    },
  },
  {
    path: "/drivers",
    name: "Drivers",
    component: DriversPage,
    meta: { requiresAuth: true, title: "Водители" },
  },
  {
    path: "/drivers/:id",
    name: "DriverDetails",
    component: () => import("@/views/DriverDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Водитель" },
    props: true,
  },
  {
    path: "/drivers/create",
    name: "DriverCreate",
    component: () => import("@/views/DriverCreatePage.vue"),
    meta: { requiresAuth: true, title: "Новый водитель" },
  },
  {
    path: "/repairs",
    name: "Repairs",
    component: () => import("@/views/RepairsPage.vue"),
    meta: { requiresAuth: true, title: "Ремонт" },
  },
  {
    path: "/fleet/calendar",
    name: "FleetCalendar",
    component: () => import("@/views/FleetCalendarPage.vue"),
    meta: { requiresAuth: true, title: "Календарь" },
  },
  {
    path: "/planning",
    name: "Planning",
    component: () => import("@/views/PlanningPage.vue"),
    meta: { requiresAuth: true, title: "План" },
  },
  {
    path: "/fleet/:view?",
    name: "Fleet",
    component: () => import("@/views/FleetPage.vue"),
    meta: { requiresAuth: true, title: "Автопарк" },
  },
  {
    path: "/vehicle-inspections",
    name: "VehicleInspections",
    component: () => import("@/views/VehicleInspectionsPage.vue"),
    meta: { requiresAuth: true, title: "Акты приемки-передачи" },
  },
  {
    path: "/vehicles",
    name: "Vehicles",
    component: VehiclesPage,
    meta: { requiresAuth: true, title: "Транспорт" },
  },
  {
    path: "/settings/vehicle_documents",
    name: "VehicleDocuments",
    component: () => import("@/views/VehicleDocumentsPage.vue"),
    meta: { requiresAuth: true, title: "Документы ТС" },
  },
  {
    path: "/vehicles/:id",
    name: "VehicleDetails",
    component: () => import("@/views/VehicleDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Транспорт" },
    props: true,
  },
  {
    path: "/vehicles/create",
    name: "VehicleCreate",
    component: () => import("@/views/VehicleCreatePage.vue"),
    meta: { requiresAuth: true, title: "Новый транспорт" },
  },
  {
    path: "/expenses",
    name: "Expenses",
    component: ExpensesPage,
    meta: { requiresAuth: true, title: "Расходы" },
  },
  {
    path: "/settings/payment_sources",
    name: "PaymentSources",
    component: PaymentSourcesPage,
    meta: { requiresAuth: true, title: "Источники оплаты" },
  },
  {
    path: "/settings/permission_roles",
    name: "PermissionRoles",
    component: PermissionRolesPage,
    meta: { requiresAuth: true, title: "Роли и права" },
  },
  {
    path: "/settings/permission_roles/:id",
    name: "PermissionRoleDetails",
    component: PermissionRoleDetailsPage,
    meta: { requiresAuth: true, title: "Роль и права" },
    props: true,
  },
  {
    path: "/settings/expense_categories",
    name: "ExpenseCategories",
    component: ExpenseCategoriesPage,
    meta: { requiresAuth: true, title: "Категории расходов" },
  },
  {
    path: "/settings",
    name: "Settings",
    component: SettingsPage,
    meta: { requiresAuth: true, title: "Настройки" },
  },
  {
    path: "/settings/attachments",
    name: "CompanyAttachments",
    component: () => import("@/views/CompanyAttachmentsPage.vue"),
    meta: { requiresAuth: true, title: "Настройки | Файлы компании" },
  },
  {
    path: "/settings/telemetry",
    name: "TelemetrySettings",
    component: () => import("@/views/TelemetrySettingsPage.vue"),
    meta: { requiresAuth: true, title: "Настройки | Телеметрия" },
  },
  {
    path: "/integrations/trackers/:trackerId/sensors",
    name: "TrackerSensors",
    component: () => import("@/views/TrackerSensorsPage.vue"),
    meta: { requiresAuth: true, title: "Датчики трекера" },
  },
  {
    path: "/integrations/trackers/:trackerId/sensors/:sensorId",
    name: "TrackerSensorDetails",
    component: () => import("@/views/TrackerSensorDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Настройка датчика" },
  },
  {
    path: "/integrations",
    name: "Integrations",
    component: IntegrationsPage,
    meta: { requiresAuth: true, title: "Интеграции" },
  },
  {
    path: "/integrations/trackers",
    name: "IntegrationTrackers",
    component: () => import("@/views/IntegrationDetailsPage.vue"),
    props: { integrationKey: "trackers" },
    meta: { requiresAuth: true, title: "Трекеры" },
  },
  {
    path: "/integrations/saby",
    name: "IntegrationSaby",
    component: () => import("@/views/IntegrationDetailsPage.vue"),
    props: { integrationKey: "saby" },
    meta: { requiresAuth: true, title: "Saby (ЭДО)" },
  },
  {
    path: "/integrations/chats",
    name: "IntegrationChats",
    component: () => import("@/views/IntegrationDetailsPage.vue"),
    props: { integrationKey: "chats" },
    meta: { requiresAuth: true, title: "Чаты" },
  },
  {
    path: "/integrations/rn-card",
    name: "IntegrationRnCard",
    component: () => import("@/views/IntegrationDetailsPage.vue"),
    props: { integrationKey: "rn-card" },
    meta: { requiresAuth: true, title: "РН-Карт" },
  },
  {
    path: "/edo/documents",
    name: "EdoDocuments",
    component: EdoDocumentsPage,
    meta: { requiresAuth: true, title: "ЭДО" },
  },
  {
    path: "/edo/documents/consignment-note/new",
    name: "EdoConsignmentNoteBuild",
    component: () =>
      import("@/components/Edo/ConsignmentNote/Form.vue"),
    meta: edoFormMeta("ЭТрН"),
  },
  {
    path: "/edo/documents/title1/new",
    redirect: (to) => ({
      name: "EdoConsignmentNoteBuild",
      query: to.query,
    }),
  },
  {
    path: "/edo/documents/transport-order/new",
    name: "EdoTransportOrderBuild",
    component: () =>
      import("@/components/Edo/TransportOrder/Form.vue"),
    meta: edoFormMeta("Заказ-заявка"),
  },
  {
    path: "/edo/documents/transport-order/:id/edit",
    name: "EdoTransportOrderEdit",
    component: () =>
      import("@/components/Edo/TransportOrder/Form.vue"),
    props: true,
    meta: edoFormMeta("Заказ-заявка"),
  },
  {
    path: "/edo/documents/transport-order/:id/title2/edit",
    name: "EdoTransportOrderTitle2Edit",
    component: () =>
      import("@/components/Edo/TransportOrder/Form.vue"),
    props: true,
    meta: edoFormMeta("Титул 2 ЭЗЗ"),
  },
  {
    path: "/edo/documents/forwarder-order/new",
    name: "EdoForwarderOrderBuild",
    component: () =>
      import("@/components/Edo/ForwarderOrder/Form.vue"),
    meta: edoFormMeta("Поручение экспедитору"),
  },
  {
    path: "/edo/documents/forwarder-order/:id/edit",
    name: "EdoForwarderOrderEdit",
    component: () =>
      import("@/components/Edo/ForwarderOrder/Form.vue"),
    props: true,
    meta: edoFormMeta("Поручение экспедитору"),
  },
  {
    path: "/edo/documents/forwarder-order/:id/title2/edit",
    name: "EdoForwarderOrderTitle2Edit",
    component: () =>
      import("@/components/Edo/ForwarderOrder/Form.vue"),
    props: true,
    meta: edoFormMeta("Титул экспедитора"),
  },
  {
    path: "/edo/documents/forwarder-receipt/new",
    name: "EdoForwarderReceiptBuild",
    component: () =>
      import("@/components/Edo/ForwarderReceipt/Form.vue"),
    meta: edoFormMeta("Экспедиторская расписка"),
  },
  {
    path: "/edo/documents/forwarder-receipt/:id/edit",
    name: "EdoForwarderReceiptEdit",
    component: () =>
      import("@/components/Edo/ForwarderReceipt/Form.vue"),
    props: true,
    meta: edoFormMeta("Экспедиторская расписка"),
  },
  {
    path: "/edo/documents/consignment-note/:id/title2/edit",
    name: "EdoConsignmentNoteTitle2Edit",
    component: () =>
      import("@/components/Edo/ConsignmentNote/Form.vue"),
    props: true,
    meta: edoFormMeta("Титул 2 ЭТрН"),
  },
  {
    path: "/edo/documents/consignment-note/:id/title3/edit",
    name: "EdoConsignmentNoteTitle3Edit",
    component: () =>
      import("@/components/Edo/ConsignmentNote/Form.vue"),
    props: true,
    meta: edoFormMeta("Титул 3 ЭТрН"),
  },
  {
    path: "/edo/documents/consignment-note/:id/title4/edit",
    name: "EdoConsignmentNoteTitle4Edit",
    component: () =>
      import("@/components/Edo/ConsignmentNote/Form.vue"),
    props: true,
    meta: edoFormMeta("Титул 4 ЭТрН"),
  },
  {
    path: "/edo/documents/consignment-note/:id/redirect/edit",
    name: "EdoConsignmentNoteRedirectEdit",
    component: () =>
      import("@/components/Edo/ConsignmentNote/Form.vue"),
    props: true,
    meta: edoFormMeta("Титул 7 ЭТрН"),
  },
  {
    path: "/edo/documents/consignment-note/:id/replacement/edit",
    name: "EdoConsignmentNoteReplacementEdit",
    component: () =>
      import("@/components/Edo/ConsignmentNote/Form.vue"),
    props: true,
    meta: edoFormMeta("Замена водителя/ТС ЭТрН"),
  },
  {
    path: "/edo/documents/:id",
    name: "EdoDocumentDetails",
    component: EdoDocumentDetailsPage,
    props: true,
    meta: { requiresAuth: true, title: "Документ ЭДО" },
  },
  {
    path: "/telemetry",
    name: "Telemetry",
    component: () => import("@/views/TelemetryMapPage.vue"),
    meta: { requiresAuth: true, title: "Мониторинг" },
  },
  {
    path: "/settings/requisite_types",
    name: "RequisiteTypes",
    component: RequisiteTypesPage,
    meta: { requiresAuth: true, title: "Настройки | Документы" },
  },
  {
    path: "/user_profile",
    name: "UserProfile",
    component: () => import("@/views/UserProfilePage.vue"),
    meta: { requiresAuth: true, title: "Профиль" },
  },
  {
    path: "/settings/templates",
    name: "Templates",
    component: TemplatesPage,
    meta: { requiresAuth: true, title: "Шаблоны документов" },
    props: true,
  },
  {
    path: "/settings/vehicle_inspections",
    name: "VehicleInspectionSettings",
    component: () => import("@/views/VehicleInspectionSettingsPage.vue"),
    meta: { requiresAuth: true, title: "Настройки | Акты приемки-передачи" },
  },
  {
    path: "/settings/templates/:id",
    name: "TemplateDetails",
    component: () => import("@/views/TemplateDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Шаблон документа" },
    props: true,
  },
  {
    path: "/settings/products",
    name: "ProductsRegistry",
    component: ProductsPage,
    meta: { requiresAuth: true, title: "Настройки | Продукты" },
  },
  {
    path: "/settings/catalog_items",
    name: "CatalogItems",
    component: CatalogItemsPage,
    meta: {
      requiresAuth: true,
      title: "Настройки | Каталог",
    },
  },
  {
    path: "/settings/cargo_items",
    name: "CargoItems",
    component: CargoItemRegistriesPage,
    meta: { requiresAuth: true, title: "Настройки | Позиции груза" },
  },
  {
    path: "/settings/cargo_units",
    name: "CargoUnits",
    component: CargoUnitsPage,
    meta: { requiresAuth: true, title: "Настройки | Типы груза" },
  },
  {
    path: "/settings/cargo_properties",
    name: "CargoProperties",
    component: CargoPropertiesPage,
    meta: {
      requiresAuth: true,
      title: "Настройки | Характеристики груза",
    },
  },
  {
    path: "/settings/reference_items",
    name: "ReferenceItems",
    component: ReferenceItemsPage,
    meta: { requiresAuth: true, title: "Настройки | Справочники" },
  },
  {
    path: "/login",
    name: "Login",
    component: LoginPage,
    meta: { requiresAuth: false, title: "Логин" },
  },
  {
    path: "/account_recovery",
    name: "AccountRecovery",
    component: AccountRecoveryPage,
    meta: { requiresAuth: false, title: "Восстановление пароля" },
  },
  {
    path: "/password_recovery",
    name: "PasswordRecovery",
    component: PasswordRecoveryPage,
    meta: { requiresAuth: false, title: "Восстановление пароля" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.afterEach((to) => {
  document.title = to.meta.title || "Трако";
  useChatStore().syncChatUnreadTitle();
});

router.onError((error) => {
  // Vue Router загружает часть страниц lazy chunks. После deploy старая
  // открытая вкладка может ссылаться на chunk из предыдущего release, которого
  // уже нет в `current/dist`. В этом случае router получает ошибку загрузки
  // модуля и без обработки пользователь остается на белом экране.
  //
  // Серверный nginx-fix должен возвращать `404` для отсутствующего `.js`, а не
  // `index.html`. Эта клиентская страховка превращает такую ошибку в один
  // controlled reload, чтобы браузер забрал новый `index.html` и актуальные
  // имена assets.
  reloadPageOnceAfterChunkLoadError(error);
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const notificationStore = useNotificationStore();

  if (to.query.__token) {
    try {
      await authStore.loginWithToken(to.query.__token);
    } catch {
      next({ name: "Login" });
      return;
    }
    const { __token, ...query } = to.query;
    next({ path: "/", query });
    return;
  }

  // Функция проверки, истек ли токен
  const isTokenExpired = () => {
    const exp = getCookie("exp");
    if (!exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return parseInt(exp) < currentTime;
  };

  const isAuthValid = authStore.isAuthenticated && !isTokenExpired();

  const recoveryRoutes = ["AccountRecovery", "PasswordRecovery"];

  if (recoveryRoutes.includes(to.name) && isAuthValid) {
    authStore.logout();
    next();
    return;
  }

  if (to.meta.requiresAuth && !isAuthValid) {
    if (getCookie("exp") && isTokenExpired()) {
      notificationStore.showWarning(
        "Сеанс закрыт. Пожалуйста, авторизуйтесь снова."
      );
      authStore.logout();
    }

    // Запоминаем путь, куда пытался попасть пользователь
    authStore.setReturnUrl(to.fullPath);
    next({ name: "Login" });
    return;
  }

  if (to.meta.forbidden) {
    next({ name: "Forbidden" });
    return;
  }

  if (to.meta.debugOnly && !isDebugFeatureEnabled()) {
    next({ name: "Forbidden" });
    return;
  }

  if (to.name === "Login" && isAuthValid) {
    next({ name: "Home" });
    return;
  }

  // Если авторизован - ждём загрузки пермишенов перед проверкой
  if (isAuthValid) {
    const userDataStore = useUserDataStore();
    await userDataStore.loadUserData();

    const hasCompany = Boolean(userDataStore.currentCompany);
    if (to.meta.requiresAuth && userDataStore.userData && !hasCompany &&
        !["NoCompanies", "UserProfile"].includes(to.name)) {
      next({ name: "NoCompanies" });
      return;
    }
    if (to.name === "NoCompanies" && hasCompany) {
      next({ name: "Home" });
      return;
    }

    const permissionsStore = usePermissionsStore();

    if (!permissionsStore.isLoaded) {
      await permissionsStore.loadPermissions();
    }

    const pageKey = PAGE_ACCESS_KEYS[to.name];

    if (pageKey && !permissionsStore.canSeePage(pageKey)) {
      next({ name: "Forbidden" });
      return;
    }

    const actionPermission = ROUTE_ACTION_PERMISSIONS[to.name];

    if (actionPermission && !can(actionPermission.resource, actionPermission.action)) {
      next({ name: "Forbidden" });
      return;
    }
  }

  next();
});

export default router;
